import { createMeta } from '@exodus/trezor-meta';
import { combine, compute, dedupe } from '@exodus/atoms';
import { mapValues, intersection } from '@exodus/basic-utils';
import { createSelector } from 'reselect';
import { WalletAccount } from '@exodus/models';
const getSupportedAssetNames = (assets, manufacturer, model) => {
    return Object.values(assets)
        .filter((asset) => asset.baseAsset.api?.features?.hardwareWallets?.supportMatrix[manufacturer]?.models.includes(model))
        .map((asset) => asset.name);
};
const trezorAvailableAssetsSelector = createSelector((state) => state.assets, (state) => state.model, (state) => state.availableAssetNames, (assets, model, availableAssetNames) => {
    const { ASSETS_BY_MODEL } = createMeta(assets);
    const legacySupportedAssetNames = new Set(ASSETS_BY_MODEL[model] || ASSETS_BY_MODEL.T);
    const supportedAssetNames = getSupportedAssetNames(assets, 'trezor', model);
    const finalSupportedAssetNames = [
        ...new Set([...legacySupportedAssetNames, ...supportedAssetNames]),
    ];
    return intersection(finalSupportedAssetNames, availableAssetNames);
});
const legacyLedgerSupportedAssets = new Set([
    'basemainnet',
    'bitcoin',
    'bitcoinregtest',
    'bitcointestnet',
    'ethereum',
    'ethereumsepolia',
    'solana',
    'matic',
]);
const ledgerAvailableAssetsSelector = createSelector((state) => state.assets, (state) => state.model, (state) => state.availableAssetNames, (assets, model, availableAssetNames) => {
    const legacySupportedAssetNames = new Set(Object.values(assets)
        .filter((asset) => legacyLedgerSupportedAssets.has(asset.baseAssetName))
        .map((asset) => asset.name));
    const supportedAssetNames = getSupportedAssetNames(assets, 'ledger', model);
    const finalSupportedAssetNames = [
        ...new Set([...legacySupportedAssetNames, ...supportedAssetNames]),
    ];
    return intersection(finalSupportedAssetNames, availableAssetNames);
});
const passkeysAvailableAssetsSelector = createSelector((state) => state.assets, (state) => state.availableAssetNames, (assets, availableAssetNames) => {
    return availableAssetNames.filter((name) => {
        const asset = assets[name];
        if (!asset) {
            return false;
        }
        const check = (asset) => {
            const baseAsset = asset.baseAsset;
            return Boolean(baseAsset.api?.features?.signWithSigner && baseAsset.api.signTx);
        };
        if (asset.isCombined) {
            return asset.combinedAssetNames.some((name) => {
                const childAsset = assets[name];
                return childAsset ? check(childAsset) : false;
            });
        }
        return check(asset);
    });
});
const createDelegatedAvailableAssetsSelector = (delegatedAllowedAssetNames) => createSelector((state) => state.availableAssetNames, (availableAssetNames) => {
    const allowedAssets = new Set(delegatedAllowedAssetNames);
    return availableAssetNames.filter((name) => allowedAssets.has(name));
});
const createAvailableAssetNamesByWalletAccountAtom = ({ assetsAtom, availableAssetNamesAtom, enabledWalletAccountsAtom, config = { delegatedAllowedAssetNames: ['solana', 'usdcoin_solana'] }, }) => {
    const delegatedAvailableAssetsSelector = createDelegatedAvailableAssetsSelector(config.delegatedAllowedAssetNames);
    return dedupe(compute({
        atom: combine({
            walletAccounts: enabledWalletAccountsAtom,
            availableAssetNames: availableAssetNamesAtom,
            assets: assetsAtom,
        }),
        selector: ({ assets, walletAccounts, availableAssetNames }) => {
            return mapValues(walletAccounts, ({ source, model, delegated }) => {
                if (delegated) {
                    return delegatedAvailableAssetsSelector({
                        availableAssetNames,
                    });
                }
                if (source === WalletAccount.TREZOR_SRC) {
                    return trezorAvailableAssetsSelector({
                        assets: assets.value,
                        model: model,
                        availableAssetNames,
                    });
                }
                if (source === WalletAccount.LEDGER_SRC) {
                    return ledgerAvailableAssetsSelector({
                        assets: assets.value,
                        model: model,
                        availableAssetNames,
                    });
                }
                if (source === WalletAccount.PASSKEY_SRC) {
                    return passkeysAvailableAssetsSelector({
                        assets: assets.value,
                        availableAssetNames,
                    });
                }
                return availableAssetNames;
            });
        },
    }));
};
export const availableAssetNamesByWalletAccountAtomDefinition = {
    id: 'availableAssetNamesByWalletAccountAtom',
    type: 'atom',
    factory: createAvailableAssetNamesByWalletAccountAtom,
    dependencies: ['assetsAtom', 'availableAssetNamesAtom', 'enabledWalletAccountsAtom', 'config?'],
    public: true,
};
